// src/domains/dash.dashboard.ts —— 06 C-02 仪表盘原生 renderVals（GET /api/admin/dashboard，spec §4.1）；经 domains/dash.ts 分派器挂载。
// 数据存模块私有 WeakMap<host, Slot>：enter 拉取（seq 比对丢弃过期响应）→ 回写后 ds.rev+1 触发重渲染；reset 换代清空（session:lost / ready / 同页重复点击）。
// 加载序：先完整求值页注册表（pages/index → 各页文件 → domains/table），再取 pages/shells 的 openUser360；
// 若经 pages/shells 首次进入，pages/index 求值时会读到未初始化的 PAGES_SHELLS（ESM TDZ）
import '../pages/index';
import { openUser360 } from '../pages/shells';
import { ApiError } from '../../../shared/src/api/client';
import type { Dict } from '../../../shared/src/dc';
import { fmtAmount, toMicro } from '../../../shared/src/money';
import { fmtDateTime, fmtMonthDay } from '../../../shared/src/time';
import { wdApprovedToast } from '../pages/funds.toast';
import type { ReviewResult } from '../pages/funds.types';
import type { DashboardResp, DashPendingRow } from '../pages/ops.types';
import { canSee, gate } from '../perm';
import type { Page } from '../state';
import type { DashPart } from './dash';
import { str, type AdminCtx } from './types';

const EMPTY = '—';
const MINUS = '−'; // 06 C-02 delta 负号 U+2212
const CODE_SAME_REVIEWER = 1501; // S-04：审核动作上的 1501 即同人二审

// 06 C-02 delta 变色：up / down / flat（原型 L849 样式串）
const DELTA_COLOR = { up: '#0d9a68', down: '#d1434b', flat: '#8a958f' } as const;
type Dir = keyof typeof DELTA_COLOR;
const deltaStyle = (d: Dir): string => `font-size:12px;font-weight:600;color:${DELTA_COLOR[d]}`;

// 最近 Webhook 状态点（06 C-02 X-17：processed 绿 / failed 红 / received 琥珀 / skipped 灰；未知值灰）
const HOOK_DOT: Readonly<Record<string, string>> = { processed: '#0d9a68', failed: '#d1434b', received: '#c47a0c', skipped: '#9aa5a1' };
// 新注册用户 KYC 章（06 C-02：none 灰 / pending 琥珀 / passed 绿 / rejected 红；原型 L935 章样式串）
const KYC_CHIP: Readonly<Record<string, readonly [string, string, string]>> = {
  none: ['stKycNone', '#eef3f0', '#6b7772'],
  pending: ['stPending', '#fff3df', '#c47a0c'],
  passed: ['stPass', '#e3f6ee', '#0d9a68'],
  rejected: ['stReject', '#fdecec', '#d1434b'],
};
const KPI_DEFS: ReadonlyArray<readonly [string, Page]> = [['kUsers', 'users'], ['kActiveCards', 'cards'], ['kVolume', 'tx'], ['kRecharge', 'wallets'], ['kFee', 'wallets'], ['kPending', 'review']];
const HEALTH_KEY: Readonly<Record<string, string>> = { green: 'healthy', amber: 'healthLow', red: 'healthBad' };
// 06 C-02 E-12 阈值色（F-14a 三档 → coverage_level）：覆盖率 绿 / 琥珀 / 红，健康文字 原型绿 / #f5a623 / #ff6b6b；null（无快照或分母 ≤ 0）灰
const COVERAGE_COLOR: Readonly<Record<string, string>> = { green: '#0d9a68', amber: '#c47a0c', red: '#d1434b' };
const HEALTH_COLOR: Readonly<Record<string, string>> = { green: '#39c88f', amber: '#f5a623', red: '#ff6b6b' };
const NULL_COLOR = '#9aa5a1';

// ---- 每宿主数据槽 ----

interface Slot {
  gen: number; // 会话代：reset +1；写动作在途结果按代丢弃
  seq: number; // 加载序号：enter / reset +1；仅最新一次加载的响应回写
  data: DashboardResp | null;
  inflight: Set<string>; // 「通过」在途锁 approve:{biz_no}
}

const slots = new WeakMap<object, Slot>();

function slotOf(host: object): Slot {
  let s = slots.get(host);
  if (!s) {
    s = { gen: 0, seq: 0, data: null, inflight: new Set() };
    slots.set(host, s);
  }
  return s;
}

function bump(ctx: AdminCtx): void {
  const ds = ctx.host.state.ds;
  ctx.host.setState({ ds: { ...ds, rev: ds.rev + 1 } });
}

function load(ctx: AdminCtx): void {
  const host = ctx.host;
  const slot = slotOf(host);
  const my = ++slot.seq;
  void ctx.gp.api
    .call<DashboardResp>('GET /api/admin/dashboard')
    .then((d) => {
      if (slots.get(host) !== slot || slot.seq !== my) return;
      slot.data = d;
      bump(ctx);
    })
    .catch((e: unknown) => {
      if (slots.get(host) !== slot || slot.seq !== my) return;
      // C-00：网络 / 5xx / 403 → toast（字典按回调时刻取，语言可能已切换）；保留上一次成功数据
      ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

// ---- 取值格式 ----

function micro(v: string | null | undefined): bigint | null {
  if (v === null || v === undefined) return null;
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

// 金额：D8-12 截断两位、千分位；非法值原样（不抛错）；null → —
function usd(v: string | null | undefined): string {
  if (v === null || v === undefined) return EMPTY;
  try {
    return `$${fmtAmount(v, 2)}`;
  } catch {
    return `$${v}`;
  }
}

const count = (n: number): string => (Number.isFinite(n) ? Math.trunc(n).toLocaleString('en-US') : EMPTY);

// 06 C-02 delta 规则：(今日 − 昨日同时段) ÷ 昨日同时段 × 100，1 位小数（四舍五入，远离零）；正 `+`、负 U+2212；昨日同时段为 0 或非法 → —
export function pctDelta(value: string, ydaySame: string): { text: string; dir: Dir } {
  const v = micro(value);
  const y = micro(ydaySame);
  if (v === null || y === null || y === 0n) return { text: EMPTY, dir: 'flat' };
  const num = (v - y) * 1000n;
  const den = y < 0n ? -y : y;
  const neg = num < 0n;
  const abs = neg ? -num : num;
  const tenths = (abs * 2n + den) / (2n * den);
  if (tenths === 0n) return { text: '0.0%', dir: 'flat' };
  const body = `${tenths / 10n}.${tenths % 10n}%`;
  return neg ? { text: `${MINUS}${body}`, dir: 'down' } : { text: `+${body}`, dir: 'up' };
}

// F-14a 比值 → 百分比整数（截断）；null / 非法 → null
export function coveragePct(ratio: string | null): string | null {
  const m = micro(ratio);
  if (m === null) return null;
  return `${(m * 100n) / 1_000_000n}%`;
}

const hhmm = (iso: string, tz: string | undefined): string => {
  const s = fmtDateTime(iso, { tz });
  return s === EMPTY ? EMPTY : s.slice(11);
};

// 'YYYY-MM-DD'（timezone 日键）→ M/D；非法 → —
function dayLabel(date: string | undefined): string {
  const m = date ? /^\d{4}-(\d{2})-(\d{2})$/.exec(date) : null;
  return m && m[1] && m[2] ? `${Number(m[1])}/${Number(m[2])}` : EMPTY;
}

// ---- 跳转与写动作 ----

// 06 C-02 kFee（0047）：有 profit.view 跳 C-32 利润台账（手续费为用户实付口径，平台净收入看台账），否则仍跳 wallets
function feePage(ctx: AdminCtx): Page {
  return canSee(ctx.s.sh.me, 'profit') ? 'profit' : 'wallets';
}

function go(ctx: AdminCtx, page: Page) {
  return (e?: { preventDefault(): void }): void => {
    e?.preventDefault();
    // 06 C-02：目标页无 view 不响应点击
    if (canSee(ctx.s.sh.me, page)) ctx.host.setState({ page, tab: 0 });
  };
}

type ApproveRoute = 'POST /api/admin/reviews/deposit/{biz_no}/approve' | 'POST /api/admin/reviews/withdrawal/{biz_no}/approve';

// 待审核快捷「通过」（06 C-02 / C-08）：review.approve 门控（模板按钮无置灰样式槽，偏差 PB-1：不足时只提示权限文案、不发请求）；
// 在途锁 approve:{biz_no}；成功 toast 按 kind / stage；1501 → errSameReviewer；成功后重拉 dashboard 并刷新侧栏徽标
async function approve(ctx: AdminCtx, r: DashPendingRow): Promise<void> {
  const g = gate(ctx.s.sh.me, ctx.t, 'review', 'approve');
  if (g.disabled) {
    ctx.gp.ui.toast(g.title);
    return;
  }
  const route: ApproveRoute | null = r.kind === 'deposit' ? 'POST /api/admin/reviews/deposit/{biz_no}/approve' : r.kind === 'withdrawal' ? 'POST /api/admin/reviews/withdrawal/{biz_no}/approve' : null;
  if (!route) return;
  const host = ctx.host;
  const slot = slotOf(host);
  const key = `approve:${r.biz_no}`;
  if (slot.inflight.has(key)) return;
  slot.inflight.add(key);
  const gen = slot.gen;
  const alive = (): boolean => slots.get(host) === slot && slot.gen === gen;
  try {
    let res: ReviewResult;
    try {
      res = await ctx.gp.api.call<ReviewResult>(route, { path: { biz_no: r.biz_no }, body: { note: '' } });
    } catch (e) {
      if (!alive()) return;
      const t = ctx.gp.ui.dict();
      ctx.gp.ui.toast(e instanceof ApiError && e.code === CODE_SAME_REVIEWER ? str(t, 'errSameReviewer') : ctx.gp.ui.err(e));
      return;
    }
    if (!alive()) return;
    const t = ctx.gp.ui.dict();
    ctx.gp.ui.toast(r.kind === 'deposit' ? str(t, 'toastApproved') : res.stage === 'first' ? str(t, 'toastFirstApproved') : wdApprovedToast(t, res));
    load(ctx);
    ctx.events.emit('profile', {});
  } finally {
    slot.inflight.delete(key);
  }
}

// ---- renderVals ----

function kpisOf(ctx: AdminCtx, d: DashboardResp | null): Array<Record<string, unknown>> {
  const t = ctx.t;
  // 原型 L848 kpiDefs：标签与跳转页逐字
  const card = (label: string, page: Page, value: string, delta: string, dir: Dir): Record<string, unknown> => ({ label: str(t, label), value, delta, go: go(ctx, page), deltaStyle: deltaStyle(dir) });
  // 未取到数据（加载中 / 失败）：值与 delta 均「—」、flat 色（06 C-02 delta 规则）
  if (!d) return KPI_DEFS.map(([l, p]) => card(l, l === 'kFee' ? feePage(ctx) : p, EMPTY, EMPTY, 'flat'));
  const k = d.kpi;
  // 计数类恒 up 色（`+0 today` 时 flat）
  const cnt = (label: string, page: Page, c: { value: number; today: number }): Record<string, unknown> => card(label, page, count(c.value), `+${count(c.today)} today`, c.today > 0 ? 'up' : 'flat');
  const amt = (label: string, page: Page, a: { value: string; yday_same: string }): Record<string, unknown> => {
    const dl = pctDelta(a.value, a.yday_same);
    return card(label, page, usd(a.value), dl.text, dl.dir);
  };
  return [
    cnt('kUsers', 'users', k.users),
    cnt('kActiveCards', 'cards', k.active_cards),
    amt('kVolume', 'tx', k.volume),
    amt('kRecharge', 'wallets', k.recharge),
    amt('kFee', feePage(ctx), k.fee),
    card('kPending', 'review', count(k.pending.value), str(t, 'review'), 'flat'),
  ];
}

// 06 C-02 图表：14 组双序列，柱高按两序列全局最大值归一化（原型 L851 样式串），hover title 为 $ 金额
function chartOf(d: DashboardResp | null): Array<Record<string, string>> {
  const pts = d?.series14 ?? [];
  const vals = pts.map((p) => [micro(p.recharge) ?? 0n, micro(p.spend) ?? 0n] as const);
  let top = 0n;
  for (const [a, b] of vals) {
    if (a > top) top = a;
    if (b > top) top = b;
  }
  const pct = (v: bigint): string => (top > 0n && v > 0n ? String(Number((v * 10_000n) / top) / 100) : '0');
  return pts.map((p, i) => {
    const [a, b] = vals[i] ?? [0n, 0n];
    return {
      v1: usd(p.recharge),
      v2: usd(p.spend),
      s1: `height:${pct(a)}%;background:#0d9a68;border-radius:4px 4px 0 0`,
      s2: `height:${pct(b)}%;background:#c9d2ce;border-radius:0 0 3px 3px`,
    };
  });
}

// 资金健康度（F-14a/b/c）：任一项 null → —，不整卡失败；代付额度恒拼「同步于 HH:mm」，stale 追加同步失败提示
// 模板 0073 dev:D24 块：健康文字 / 覆盖率颜色由 healthStyle / coverageStyle 下发；钱包位标签（liabilitySum）与净入金行标签（netInflow）为 ops 包键、
// 不在原型字典，模板不得直引 t.*（build split.test 双语字典守卫），改由 walletLabel / netInflowLabel 按当前字典下发
const healthStyle = (color: string): string => `color:${color};font-weight:700`;
const coverageStyle = (color: string): string => `color:${color}`;

function fhOf(t: Dict, d: DashboardResp | null, tz: string | undefined): Record<string, string> {
  const labels = { walletLabel: str(t, 'liabilitySum'), netInflowLabel: str(t, 'netInflow') };
  if (!d) {
    return { ...labels, merchant: EMPTY, frozen: EMPTY, health: EMPTY, healthStyle: healthStyle(NULL_COLOR), wallet: EMPTY, card: EMPTY, coverage: EMPTY, coverageStyle: coverageStyle(NULL_COLOR), netInflow: EMPTY, quota: EMPTY };
  }
  const f = d.fund_health;
  const lv = f.coverage_level;
  const level = lv ? HEALTH_KEY[lv] : undefined;
  const coverage = coveragePct(f.coverage);
  const q = f.payout_quota;
  const quota = q ? `${usd(q.left)} · ${str(t, 'syncedAt')} ${hhmm(q.synced_at, tz)}${q.stale ? ` · ${str(t, 'obsQuotaStale')}` : ''}` : EMPTY;
  return {
    ...labels,
    merchant: usd(f.merchant_usd?.balance),
    frozen: usd(f.merchant_usd?.frozen),
    health: level ? str(t, level) : EMPTY,
    healthStyle: healthStyle((lv && HEALTH_COLOR[lv]) || NULL_COLOR),
    wallet: usd(f.liability),
    card: usd(f.card_available_sum),
    coverage: coverage ?? EMPTY,
    coverageStyle: coverageStyle((coverage !== null && lv && COVERAGE_COLOR[lv]) || NULL_COLOR),
    netInflow: usd(f.net_inflow),
    quota,
  };
}

function pendingOf(ctx: AdminCtx, d: DashboardResp | null, tz: string | undefined): Array<Record<string, unknown>> {
  const t = ctx.t;
  return (d?.pending_top ?? []).slice(0, 4).map((r) => {
    const dep = r.kind === 'deposit';
    const time = fmtDateTime(r.created_at, { tz });
    let amount: string;
    try {
      amount = `${fmtAmount(r.amount, 2)} USDT`;
    } catch {
      amount = `${r.amount} USDT`;
    }
    return {
      kind: dep ? '↓' : '↑',
      // 原型 L934 徽标样式串逐字
      badge: `width:30px;height:30px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;font-weight:800;flex:none;background:${dep ? '#e3f6ee' : '#ece9fb'};color:${dep ? '#0d9a68' : '#6b4fd8'}`,
      user: r.user_label,
      // 已一审、待二审的提币行：副文本「待二审」（S-04；模板该位为时间行）
      time: r.first_approved ? `${time} · ${str(t, 'waitSecondReview')}` : time,
      amount,
      approve: () => void approve(ctx, r),
    };
  });
}

function newUsersOf(ctx: AdminCtx, d: DashboardResp | null): Array<Record<string, unknown>> {
  const t = ctx.t;
  return (d?.new_users ?? []).slice(0, 4).map((u) => {
    const email = u.email ?? (u.telegram_id !== null ? `TG ${u.telegram_id}` : u.uid);
    const k = KYC_CHIP[u.kyc_status];
    return {
      email,
      agent: u.agent_label ?? EMPTY,
      initial: email.charAt(0).toUpperCase(),
      kyc: k ? str(t, k[0]) : u.kyc_status,
      chip: `padding:3px 8px;border-radius:999px;font-size:11px;font-weight:700;flex:none;background:${k ? k[1] : '#eef3f0'};color:${k ? k[2] : '#6b7772'}`,
      open: () => openUser360(ctx, u.id),
    };
  });
}

function hooksOf(d: DashboardResp | null, tz: string | undefined): Array<Record<string, string>> {
  return (d?.recent_webhooks ?? []).slice(0, 5).map((h) => ({
    type: h.event_type,
    time: hhmm(h.received_at, tz),
    dot: `width:8px;height:8px;border-radius:50%;flex:none;background:${HOOK_DOT[h.status] ?? '#9aa5a1'}`,
  }));
}

function vals(ctx: AdminCtx): Record<string, unknown> {
  const t = ctx.t;
  const tz = ctx.s.sh.timezone || undefined;
  const d = slots.get(ctx.host)?.data ?? null;
  const pts = d?.series14 ?? [];
  const now = Date.now();
  const pendingList = pendingOf(ctx, d, tz);
  const newUsers = newUsersOf(ctx, d);
  const recentHooks = hooksOf(d, tz);
  return {
    kpis: kpisOf(ctx, d),
    chart: chartOf(d),
    // x 轴两端：13 天前 → 今日（series14 日键；无数据时按当前时刻）
    chartStart: pts.length > 0 ? dayLabel(pts[0]?.date) : fmtMonthDay(new Date(now - 13 * 24 * 3600e3).toISOString(), tz),
    chartEnd: pts.length > 0 ? dayLabel(pts[pts.length - 1]?.date) : fmtMonthDay(new Date(now).toISOString(), tz),
    pendingList,
    newUsers,
    recentHooks,
    pendingEmpty: pendingList.length === 0,
    newUsersEmpty: newUsers.length === 0,
    hooksEmpty: recentHooks.length === 0,
    fh: fhOf(t, d, tz),
    goReview: go(ctx, 'review'),
    goUsers: go(ctx, 'users'),
    goLogs: go(ctx, 'logs'),
    goUpstream: go(ctx, 'upstream'),
  };
}

export const DASH_DASHBOARD: DashPart = {
  vals,
  enter: (ctx) => load(ctx),
  reset: (host) => {
    const s = slotOf(host);
    s.gen++;
    s.seq++;
    s.data = null;
  },
};
